import { useEffect, useState, type ComponentType, type ReactElement, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/stores/toast.store'
import { submitJiraReport, JiraReportType, type JiraReportValue } from '@/api/profile/profile.api'
import {
  IconClose20,
  IconHelp20,
  IconLogout,
  IconProfile,
  IconSettings,
  // IconStudent,
} from '@/components/ui/icons'
import { AccountTab } from '@/components/settings/AccountTab'
import { AboutPage } from '@/components/settings/AboutPage'
import { FeedbackForm } from '@/components/settings/FeedbackForm'
import { HelpTab, type HelpSubPage } from '@/components/settings/HelpTab'
import { MyStudiesTab, type StudiesSubPage } from '@/components/settings/MyStudiesTab'
import { SubjectsPage } from '@/components/settings/SubjectsPage'
import { PanelHeader } from '@/components/settings/panel-rows'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { EditWidgetsTab } from '@/components/settings/EditWidgetsTab'
import { useAuth } from '@/hooks/useAuth'
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/i18n/config'
import { useUiStore, type ThemeMode } from '@/stores/ui.store'
import { cn } from '@/lib/utils'
import { DropdownSelect } from '../ui/dropdown'

type TabId = 'account' | 'studies' | 'settings' | 'help'
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>

/** Figma `panel-list` — a 16px-padded row with a bottom rule. */
function PanelRow({
  title,
  control,
  last = false,
}: {
  title: string
  control?: ReactElement
  last?: boolean
}): ReactElement {
  return (
    <div
      data-name="panel-list"
      className={cn('flex w-full items-center gap-4 pb-4', !last && 'hub-rule')}
    >
      <div className="flex min-w-px flex-1 flex-col items-start gap-1">
        <p className="hub-title-lg text-list-text w-full">{title}</p>
      </div>
      {control}
    </div>
  )
}

/** Figma `button-sm` with a trailing up/down chevron, wrapping a real select. */
function SelectControl<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  label: string
}): ReactElement {
  return <DropdownSelect label={label} value={value} options={options} onChange={onChange} />
}

interface SettingsPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * User panel — Figma `panel` (2256:285290). 700×440 centred from 900px up,
 * with the tab list on the left; below that it fills the viewport (inset 12px)
 * and the tabs become a scrollable bar across the top.
 */
export function SettingsPanel({ open, onClose }: SettingsPanelProps): ReactElement | null {
  const { t, i18n } = useTranslation()
  const { logout, user } = useAuth()
  const theme = useUiStore((state) => state.theme)
  const setTheme = useUiStore((state) => state.setTheme)
  const setLanguage = useUiStore((state) => state.setLanguage)

  const [tab, setTab] = useState<TabId>('account')
  // The personalization sub-page replaces the tab body, as in the design.
  const [editingWidgets, setEditingWidgets] = useState(false)
  const [studiesPage, setStudiesPage] = useState<StudiesSubPage | null>(null)
  const [helpPage, setHelpPage] = useState<HelpSubPage | null>(null)
  const [confirmLogout, setConfirmLogout] = useState(false)

  useEffect(() => {
    if (!open) {
      document.body.classList.remove('overflow-hidden')

      setTab('account')
      setEditingWidgets(false)
      setStudiesPage(null)
      setHelpPage(null)
      setConfirmLogout(false)
      return
    }

    document.body.classList.add('overflow-hidden')

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKeyDown)

    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
      document.body.classList.remove('overflow-hidden')
    }
  }, [open, onClose])

  if (!open) return null

  const tabs: { id: TabId; label: string; Icon: IconComponent }[] = [
    { id: 'account', label: t('settings.account'), Icon: IconProfile },
    // { id: 'studies', label: t('settings.studies'), Icon: IconStudent },
    { id: 'settings', label: t('settings.settings'), Icon: IconSettings },
    { id: 'help', label: t('settings.help'), Icon: IconHelp20 },
  ]

  const themeOptions: { value: ThemeMode; label: string }[] = [
    { value: 'system', label: t('settings.themeSystem') },
    { value: 'light', label: t('settings.themeLight') },
    { value: 'dark', label: t('settings.themeDark') },
  ]

  const languageOptions = SUPPORTED_LANGUAGES.map((language) => ({
    value: language,
    label: t(`language.${language}`),
  }))

  const changeLanguage = (language: SupportedLanguage): void => {
    void i18n.changeLanguage(language)
    setLanguage(language)
  }

  const menuItemClass = (active: boolean): string =>
    cn(
      'flex cursor-pointer shrink-0 items-center gap-2 rounded-xs py-2 pr-3 pl-2 transition-colors md:w-full',
      active ? 'bg-white dark:bg-panel-selected' : 'hover:bg-white dark:hover:bg-panel-selected',
    )

  const handleFeedbackSubmit = async (
    message: string,
    reportType: JiraReportValue,
    attachments: File[],
  ): Promise<void> => {
    try {
      const userEmail = user?.email ?? ''

      await submitJiraReport({
        emailAddress: userEmail,
        text: message,
        attachments,
        type: reportType,
      })

      toast.success(t('help.successMessage') || 'Sikeresen elküldve!')
      setHelpPage(null)
    } catch (error) {
      console.error('Hiba a beküldéskor:', error)
      toast.error(
        t('help.errorMessage', {
          defaultValue: 'Nem sikerült elküldeni. Kérjük, próbáld újra.',
        }),
      )
    }
  }

  return (
    <>
      <div
        data-name="bg-overlay"
        className="bg-overlay fixed inset-0 z-50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        data-node-id="2256:285290"
        data-name="panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('settings.title')}
        className={cn(
          'hub-user-panel fixed z-50 flex flex-col items-start overflow-hidden rounded-[12px]',
          'inset-3',
          'md:inset-auto md:top-1/2 md:left-1/2 md:h-110 md:w-175 md:-translate-x-1/2 md:-translate-y-1/2 md:flex-row',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-[12px] after:border-2 after:border-[#DEDEDE] dark:after:border-[#333333]',
        )}
      >
        {/* panel-sidebar: top bar below 900px, side rail above */}
        <div
          data-name="panel-sidebar"
          className="bg-panel-sidebar flex w-full shrink-0 items-center gap-3 p-3 md:h-full md:w-40 md:flex-col md:items-start"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="text-panel-text dark:hover:bg-panel-selected order-2 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-xs p-2 transition-colors hover:bg-white md:order-1"
          >
            <IconClose20 className="size-5" aria-hidden="true" />
          </button>

          <div
            data-name="menu-items"
            className="order-1 flex min-w-px flex-1 items-center overflow-x-auto md:order-2 md:w-34 md:flex-none md:flex-col md:items-start md:overflow-visible"
          >
            {tabs.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setTab(id)
                  setEditingWidgets(false)
                  setStudiesPage(null)
                  setHelpPage(null)
                }}
                className={menuItemClass(tab === id)}
              >
                <Icon className="text-panel-text size-5 shrink-0" aria-hidden="true" />
                <span
                  className={cn(
                    'text-panel-text whitespace-nowrap',
                    tab === id ? 'hub-label-md-highlight' : 'hub-label-md',
                  )}
                >
                  {label}
                </span>
              </button>
            ))}

            <button
              type="button"
              onClick={() => {
                setConfirmLogout(true)
              }}
              className={menuItemClass(false)}
            >
              <IconLogout className="text-panel-text size-5 shrink-0" aria-hidden="true" />
              <span className="hub-label-md text-panel-text whitespace-nowrap">
                {t('settings.logout')}
              </span>
            </button>
          </div>
        </div>

        {/* content */}
        <div
          data-name="content"
          className="flex min-w-px flex-1 flex-col items-start gap-4 self-stretch overflow-y-auto px-6 pt-4 md:h-full md:p-4"
        >
          {editingWidgets ? (
            <EditWidgetsTab
              onBack={() => {
                setEditingWidgets(false)
              }}
            />
          ) : studiesPage ? (
            studiesPage === 'subjects' ? (
              <SubjectsPage
                onBack={() => {
                  setStudiesPage(null)
                }}
              />
            ) : (
              <>
                <PanelHeader
                  compact
                  title={t(`studies.${studiesPage}`)}
                  onBack={() => {
                    setStudiesPage(null)
                  }}
                />
                <p className="hub-title-lg text-widget-secondary">{t('state.empty')}</p>
              </>
            )
          ) : helpPage === 'ideas' ? (
            <FeedbackForm
              title={t('help.ideasTitle')}
              prompt={t('help.ideasPrompt')}
              placeholder={t('help.ideasPlaceholder')}
              onBack={() => {
                setHelpPage(null)
              }}
              onSubmit={(message, attachments) =>
                handleFeedbackSubmit(message, JiraReportType.Task, attachments)
              }
            />
          ) : helpPage === 'bug' ? (
            <FeedbackForm
              title={t('help.bugTitle')}
              prompt={t('help.bugPrompt')}
              placeholder={t('help.bugPlaceholder')}
              onBack={() => {
                setHelpPage(null)
              }}
              onSubmit={(message, attachments) =>
                handleFeedbackSubmit(message, JiraReportType.Bug, attachments)
              }
            />
          ) : helpPage === 'about' ? (
            <AboutPage
              onBack={() => {
                setHelpPage(null)
              }}
            />
          ) : (
            <>
              <div className="hub-rule flex w-full items-center pb-4">
                <h2 className="hub-headline-lg text-list-text whitespace-nowrap">
                  {tabs.find((entry) => entry.id === tab)?.label}
                </h2>
              </div>

              {tab === 'account' ? (
                <AccountTab />
              ) : tab === 'studies' ? (
                <MyStudiesTab onOpen={setStudiesPage} />
              ) : tab === 'help' ? (
                <HelpTab onOpen={setHelpPage} />
              ) : (
                <>
                  <PanelRow
                    title={t('settings.appearance')}
                    control={
                      <SelectControl
                        value={theme}
                        options={themeOptions}
                        onChange={setTheme}
                        label={t('settings.appearance')}
                      />
                    }
                  />
                  <PanelRow
                    title={t('settings.language')}
                    control={
                      <SelectControl
                        value={i18n.resolvedLanguage === 'en' ? 'en' : 'hu'}
                        options={languageOptions}
                        onChange={changeLanguage}
                        label={t('settings.language')}
                      />
                    }
                  />
                  {/* <PanelLinkRow
                    last
                    label={t('settings.personalization')}
                    onOpen={() => {
                      setEditingWidgets(true)
                    }}
                  /> */}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title={t('settings.logoutTitle')}
        description={t('settings.logoutDescription')}
        confirmLabel={t('settings.logout')}
        onCancel={() => {
          setConfirmLogout(false)
        }}
        onConfirm={() => {
          setConfirmLogout(false)
          void logout()
        }}
      />
    </>
  )
}
